import { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { Select, Grid, Checkbox, Popover } from "@mantine/core";
import { useForm } from "@mantine/form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/sharp-regular-svg-icons";
import { faInfoCircle } from "@fortawesome/sharp-solid-svg-icons";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { FeatureToggle } from "shared/src/constants/feature-toggle-names";
import { useProviderName } from "@/hooks/use-provider-name";
import {
  OPTUM_FRONTIER_THERAPIES,
  PANTHERX,
} from "shared/src/data/provider-names";
import { gql } from "@/__generated__";
import Navbar from "@/components/layout/navbar";
import { Toast } from "design-system/src/components/toast";
import { PageFooter } from "@/components/layout/page-footer";
import {
  InitializeReferralInput,
  SubDenialReason,
  OtherAppealSituations,
} from "@/__generated__/graphql";
import {
  Condition,
  getConditionsTreatedByMedication,
  getMedicationsForReferral,
  isGlp1Condition,
  Medication,
} from "shared/src/data/medication-condition-pairs";
import { FdaEligibilityPopoverContent } from "@/components/fda-eligibility-popover-content/fda-eligibility-popover-content";
import { DenialReason } from "@/data/denial-reasons";
import { shouldShowSpecialSituationsField } from "shared/src/utils/special-situations-utils";
import { SpecialAppealSituationsCard } from "./special-appeal-situations-card";
import { SubDenialReasonField } from "./sub-denial-reason-field";

import styles from "./initial-referral-page.module.scss";
import "../../styles/components/landing-page.scss";
import { ReferPatientProgressTracker } from "@/components/refer-patient-progress-tracker";

const INITIALIZE_REFERRAL = gql(`
  mutation InitializeReferral($input: InitializeReferralInput!) {
    initializeReferral(input: $input) {
      appealId
      condition
      medication
      denialReason
    }
  }
`);

interface FormValues {
  condition: string;
  medication: string;
  denialReason: string;
  subDenialReasons: SubDenialReason[];
  meetsFdaGuidelines: boolean;
}

export function InitialReferralPage() {
  const navigate = useNavigate();

  const providerName = useProviderName();
  const isCsidSucraidEnabled =
    providerName?.includes(OPTUM_FRONTIER_THERAPIES) ?? false;
  const isHppStrensiqEnabled = providerName?.includes(PANTHERX) ?? false;
  const isLiverTransplantEnabled =
    useFeatureFlagEnabled(FeatureToggle.LIVER_TRANSPLANT) === true;

  const [selectedSituations, setSelectedSituations] = useState<
    OtherAppealSituations[]
  >([]);

  const form = useForm<FormValues>({
    initialValues: {
      condition: "",
      medication: "",
      denialReason: "",
      subDenialReasons: [],
      meetsFdaGuidelines: false,
    },
    validate: {
      condition: (value) => (!value ? "Medical condition is required" : null),
      medication: (value) => (!value ? "Medication is required" : null),
      denialReason: (value) => (!value ? "Denial reason is required" : null),
      meetsFdaGuidelines: (value, values) =>
        isGlp1Condition(values.condition) && !value
          ? "You must confirm the patient meets FDA guidelines for GLP-1"
          : null,
    },
  });

  const medication = form.values.medication;
  const condition = form.values.condition;

  useEffect(() => {
    if (condition && medication) {
      const availableConditions = getConditionsTreatedByMedication(
        medication as Medication,
      );
      if (!availableConditions.includes(condition)) {
        form.setFieldValue("condition", "");
      }
    }
  }, [medication]);

  const denialReason = form.values.denialReason;
  const hppDenialReasons: string[] = [
    DenialReason.MEDICAL_NECESSITY,
    DenialReason.FORMULARY,
  ];

  useEffect(() => {
    if (
      condition === Condition.HYPOPHOSPHATASIA &&
      denialReason &&
      !hppDenialReasons.includes(denialReason)
    ) {
      form.setFieldValue("denialReason", "");
    }
  }, [condition]);

  useEffect(() => {
    if (!isGlp1Condition(form.values.condition)) {
      form.setFieldValue("meetsFdaGuidelines", false);
    }
  }, [form.values.condition]);

  const shouldShowSpecialSituations = shouldShowSpecialSituationsField(
    medication,
    condition,
  );

  useEffect(() => {
    if (!shouldShowSpecialSituations) {
      setSelectedSituations([]);
    }
  }, [shouldShowSpecialSituations]);

  const medicationOptions = isHppStrensiqEnabled
    ? [Medication.STRENSIQ]
    : getMedicationsForReferral({
        conditions: {
          [Condition.PANDAS]: false,
          [Condition.PANS]: false,
          [Condition.CSID]: isCsidSucraidEnabled,
          [Condition.HYPOPHOSPHATASIA]: false,
          [Condition.ATOPIC_DERMATITIS]: false,
          [Condition.CHRONIC_RHINOSINUSITIS]: false,
          [Condition.EOSINOPHILIC_ESOPHAGITIS]: false,
          [Condition.PRURIGO_NODULARIS]: false,
          [Condition.COPD]: false,
          [Condition.CHRONIC_SPONTANEOUS_URTICARIA]: false,
          [Condition.BULLOUS_PEMPHIGOID]: false,
          [Condition.LIVER_CANCER]: isLiverTransplantEnabled,
        },
      });

  const [initializeReferral, { loading, error: initializeError }] =
    useMutation(INITIALIZE_REFERRAL);

  const toggleSituation = (situation: OtherAppealSituations) => {
    setSelectedSituations((prev) =>
      prev.includes(situation)
        ? prev.filter((s) => s !== situation)
        : [...prev, situation],
    );
  };

  const handleSubmit = async (values: FormValues) => {
    const input: InitializeReferralInput = {
      condition: values.condition,
      medication: values.medication,
      denialReason: values.denialReason,
      subDenialReasons:
        values.subDenialReasons.length > 0
          ? values.subDenialReasons
          : undefined,
      otherAppealSituations:
        selectedSituations.length > 0 ? selectedSituations : undefined,
    };

    try {
      const { data } = await initializeReferral({ variables: { input } });
      if (data?.initializeReferral) {
        const { appealId } = data.initializeReferral;
        navigate(`/refer/${appealId}/patient-info`);
      }
    } catch {}
  };

  const handleCancel = () => {
    navigate("/");
  };

  const isFormValid = () => {
    const { condition, medication, denialReason, meetsFdaGuidelines } =
      form.values;
    if (!condition || !medication || !denialReason) return false;
    if (isGlp1Condition(condition) && !meetsFdaGuidelines) return false;
    return true;
  };

  const handleFooterSubmit = async () => {
    const validationResult = form.validate();
    if (validationResult.hasErrors) {
      return;
    }
    await handleSubmit(form.getValues());
  };

  const footer = (
    <PageFooter
      secondaryButton={{
        text: "Cancel",
        onClick: handleCancel,
      }}
      primaryButton={{
        text: "Next",
        onClick: handleFooterSubmit,
        disabled: loading || !isFormValid(),
        loading,
      }}
    />
  );

  return (
    <Navbar titleComponent={<ReferPatientProgressTracker />} footer={footer}>
      <div className={styles.pageContainer}>
        <div className={styles.formContainer}>
          <h2 className={styles.formHeader}>Denial Info</h2>
          <div className={styles.formSubheader}>
            <p className={styles.formSubheader__text}>
              Tell us about the denial your patient needs to appeal. Don't see
              what you're looking for? <a href="#">Click here.</a>
            </p>
          </div>
          <hr className={styles.sectionDivider} />

          <Toast variant="error" isVisible={!!initializeError}>
            {initializeError?.message}
          </Toast>

          <form
            id="initial-referral-form"
            onSubmit={form.onSubmit(handleSubmit)}
          >
            <Grid>
              <Grid.Col>
                <Select
                  label="Medication"
                  placeholder="Look up by name"
                  data-testid="medication-input"
                  searchable
                  withAsterisk
                  disabled={loading}
                  error={form.errors.medication}
                  data={medicationOptions}
                  rightSection={
                    <FontAwesomeIcon icon={faMagnifyingGlass} size="lg" />
                  }
                  {...form.getInputProps("medication")}
                />
              </Grid.Col>
            </Grid>

            <Grid mt="md">
              <Grid.Col>
                <Select
                  label="Condition"
                  placeholder="Look up by condition"
                  data-testid="condition-input"
                  searchable
                  withAsterisk
                  disabled={loading || !medication}
                  error={form.errors.condition}
                  data={
                    medication
                      ? getConditionsTreatedByMedication(
                          medication as Medication,
                        )
                      : []
                  }
                  rightSection={
                    <FontAwesomeIcon icon={faMagnifyingGlass} size="lg" />
                  }
                  {...form.getInputProps("condition")}
                />
              </Grid.Col>
            </Grid>

            <Grid mt="md">
              <Grid.Col>
                <Select
                  label="Denial Reason"
                  placeholder="Select denial reason"
                  data-testid="denial-reason-input"
                  withAsterisk
                  disabled={loading}
                  error={form.errors.denialReason}
                  data={
                    condition === Condition.HYPOPHOSPHATASIA
                      ? hppDenialReasons
                      : Object.values(DenialReason)
                  }
                  {...form.getInputProps("denialReason")}
                />
              </Grid.Col>
            </Grid>

            <SubDenialReasonField
              condition={form.values.condition}
              denialReason={form.values.denialReason}
              subDenialReasons={form.values.subDenialReasons}
              disabled={loading}
              allowMultipleSubDenialReasons={isHppStrensiqEnabled}
              onChange={(values) =>
                form.setFieldValue("subDenialReasons", values)
              }
              onClear={() => form.setFieldValue("subDenialReasons", [])}
              error={form.errors.subDenialReasons}
            />

            {isGlp1Condition(form.values.condition) && (
              <Grid mt="md">
                <Grid.Col>
                  <div className={styles.fdaDisclaimer}>
                    <span className={styles.checkbox}>
                      <Checkbox
                        label="This patient meets the FDA guidelines for GLP-1"
                        checked={form.values.meetsFdaGuidelines}
                        onChange={(event) =>
                          form.setFieldValue(
                            "meetsFdaGuidelines",
                            event.currentTarget.checked,
                          )
                        }
                        error={form.errors.meetsFdaGuidelines}
                        classNames={{
                          inner: styles.checkboxContainer,
                          input: styles.checkboxInput,
                          icon: styles.checkboxIcon,
                        }}
                      />
                    </span>
                    <Popover
                      withArrow
                      styles={{
                        arrow: { background: "#000" },
                        dropdown: {
                          background: "#000",
                          borderRadius: "4px",
                          padding: "8px",
                        },
                      }}
                      width="350px"
                    >
                      <Popover.Target>
                        <FontAwesomeIcon
                          color="#29A7D3"
                          icon={faInfoCircle}
                          title="Info"
                        />
                      </Popover.Target>
                      <Popover.Dropdown>
                        <FdaEligibilityPopoverContent
                          condition={
                            form.values.condition as
                              | Condition.OBESITY
                              | Condition.TYPE_2_DIABETES
                          }
                        />
                      </Popover.Dropdown>
                    </Popover>
                  </div>
                </Grid.Col>
              </Grid>
            )}
          </form>
        </div>

        {shouldShowSpecialSituations && (
          <div className={styles.rightColumnContainer}>
            <SpecialAppealSituationsCard
              selectedSituations={selectedSituations}
              onToggleSituation={toggleSituation}
              disabled={loading}
            />
          </div>
        )}
      </div>
    </Navbar>
  );
}
